import React, { StrictMode as Strict, useMemo } from 'react';

import Header from '@/components/atomic/atoms/surfaces/header';
import Breadcrumbs from '@/components/atomic/templates/navigation/breadcrumbs/breadcrumbs';
import AuthoringHelper from '@/utils/authoring';
import { BeforeRenderer } from '@/utils/entry/before-renderer';
import MainRenderer from '@/utils/entry/main-renderer';
import type { EntryProps } from '@/utils/entry/typings';
import GlobalStyle from '@/utils/global-style';
import loadableRetry from '@/utils/loadable-retry';
import createCache from '@emotion/cache';
import { CacheProvider, ThemeProvider } from '@emotion/react';
import loadable from '@loadable/component';
import { EnvVarsService } from '@multitenancy-spa/env-vars-service/esm/services/env-vars-service';

const canUseMocks = EnvVarsService.getVar('VITE_USE_MOCKS', true);

const MockedRenderer = loadable(() =>
	loadableRetry(() => import('@/utils/entry/mocked-renderer')),
);

const StrictMode =
	process.env.NODE_ENV === 'development' ? React.Fragment : Strict;
const Renderer = canUseMocks ? MockedRenderer : MainRenderer;

const cache = createCache({
	key: EnvVarsService.getVar('VITE_CACHE_KEY'),
	speedy: !globalThis.prerender,
});

const Entry: React.FC<EntryProps> = ({
	theme,
	fragmentVariationPath,
	BeforeEntryComponent,
	AfterEntryComponent,
}) => {
	const AuthoringStyle = useMemo(
		() =>
			AuthoringHelper.isInEditor
				? loadable(() =>
						loadableRetry(() => import('@/utils/authoring-style')),
					)
				: undefined,
		[],
	);

	return (
		<StrictMode>
			<CacheProvider value={cache}>
				<ThemeProvider theme={theme}>
					<GlobalStyle theme={theme} />
					{AuthoringStyle ? <AuthoringStyle /> : null}
					{BeforeEntryComponent ?? null}
					<Header>
						<BeforeRenderer />
						<Renderer
							fragmentVariationPath={fragmentVariationPath}
						/>
						<Breadcrumbs />
					</Header>
					{AfterEntryComponent ?? null}
				</ThemeProvider>
			</CacheProvider>
		</StrictMode>
	);
};

export default Entry;
